import { useCallback, useState } from "react";
import {
  BooleanField,
  Button,
  DatagridConfigurable,
  DateField,
  List,
  SelectColumnsButton,
  Show,
  TabbedShowLayout,
  TabbedShowLayoutTabs,
  Tab,
  TextField,
  TopToolbar,
  useNotify,
  usePermissions,
  useRecordContext,
  useRefresh,
  useUpdate,
} from "react-admin";
import {
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  DialogActions,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { Box, Stack } from "@mui/system";
import { ContentCopy, ToggleOn, ToggleOff } from "@mui/icons-material";
import { renderIfPermissions } from "../api/permissionManager";
import { useParams } from "react-router-dom";

export const PluggablePatchList = (props) => {
  return (
    <List
      resource="pluggable_patch"
      {...props}
      pagination={false}
      exporter={false}
      actions={
        <TopToolbar>
          <SelectColumnsButton />
        </TopToolbar>
      }
    >
      <DatagridConfigurable omit={["expires_at"]} bulkActionButtons={false} size="large" rowClick={"show"}>
        <TextField source="uuid" label="UUID" />
        <BooleanField source="enabled" label="Enabled?" />
        <TextField source="created_by" />
        <DateField showTime source="created_at" />
        <DateField showTime source="expires_at" />
      </DatagridConfigurable>
    </List>
  );
};

const JSONBeautifier = ({ source, label }) => {
  const record = useRecordContext();
  const notify = useNotify();
  const [showJSON, setShowJSON] = useState(false);
  if (!record) return null;

  let beautifiedJSON = "";
  try {
    beautifiedJSON = JSON.stringify(record[source], null, 2);
  } catch (error) {
    beautifiedJSON = "Invalid JSON";
  }

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(beautifiedJSON);
      notify(`Copied ${label} to clipboard!`, { type: "success" });
    } catch (err) {
      notify(`Failed to copy ${label} to clipboard!`, { type: "error" });
      console.error("Failed to copy JSON:", err);
    }
  };

  const jsonStyle = {
    backgroundColor: "#f5f5f5",
    border: "2px solid #ddd",
    borderRadius: "1em",
    marginLeft: "1em",
    marginTop: "1em",
    padding: "1em",
    fontFamily: "monospace",
    color: "#333",
    width: "fit-content",
  };

  return (
    <div>
      <Stack direction="row" spacing={1} sx={{ marginLeft: "1em", marginTop: "1em" }}>
        <Button
          data-testid={`show-hide-${label}-btn`}
          variant="outlined"
          onClick={() => setShowJSON(!showJSON)}
          label={showJSON ? `Hide ${label}` : `View ${label}`}
        />
        {showJSON && (
          <IconButton data-testId={`copy-${label}-btn`} variant="outlined" onClick={handleCopy}>
            <ContentCopy color="primary" />
          </IconButton>
        )}
      </Stack>
      {showJSON && (
        <div data-testid={`${label}-json`} style={jsonStyle}>
          <pre>{beautifiedJSON}</pre>
        </div>
      )}
    </div>
  );
};

export const PluggablePatchShow = (props) => {
  const { permissions } = usePermissions();
  const { id } = useParams();

  return (
    <Show resource="pluggable_patch">
      <TabbedShowLayout
        tabs={<TabbedShowLayoutTabs variant="scrollable" scrollButtons="auto" {...props} />}
        spacing={2}
        style={{ paddingBottom: "16px" }}
        syncWithLocation={false}
        uuid={id}
      >
        <Tab label="Patch Information" path="info">
          <TextField source="uuid" />
          <TextField source="route_key" />
          <BooleanField source="enabled" label="Enabled?" />
          <JSONBeautifier source="patch" label="patch" />
          <JSONBeautifier source="geometry" label="geometry" />
        </Tab>
        {renderIfPermissions(
          permissions,
          "routing",
          "write",
          <Tab label="Update Patch" path="update">
            <EnableDisableToggle />
          </Tab>
        )}
      </TabbedShowLayout>
    </Show>
  );
};

const EnableDisableToggle = () => {
  const [open, setOpen] = useState(false);
  const [update, { isLoading }] = useUpdate();
  const record = useRecordContext();
  const notify = useNotify();
  const refresh = useRefresh();

  if (!record) return null;

  const isEnabled = record.enabled;
  const label = isEnabled ? "Disable Patch" : "Enable Patch";

  const handleClickOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleConfirm = useCallback(() => {
    const action = isEnabled ? "disable" : "enable";

    update(
      "pluggable_patch",
      { id: record.id, data: { enabled: !isEnabled }, previousData: record },
      {
        onSuccess: () => {
          notify(`Patch ${action}d successfully`, { type: "success" });
          refresh();
          handleClose();
        },
        onError: (error) => {
          notify(`Failed to ${action} patch: ${error.message}`, { type: "error" });
          handleClose();
        },
      }
    );
  }, [record, update, notify, refresh, isEnabled]);

  return (
    <Box>
      <Typography>
        Current Status: <b>{isEnabled ? "Enabled" : "Disabled"}</b>
      </Typography>
      <Tooltip title={label}>
        <IconButton
          data-testid={"update-patch-toggle"}
          sx={{ mx: "2em" }}
          onClick={handleClickOpen}
          disabled={isLoading}
          color={isEnabled ? "success" : "error"}
          size="large"
        >
          {isEnabled ? <ToggleOn fontSize="inherit" /> : <ToggleOff fontSize="inherit" />}
        </IconButton>
      </Tooltip>
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>{label}</DialogTitle>
        <DialogContent>
          <DialogContentText>Are you sure you want to {isEnabled ? "disable" : "enable"} this patch?</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button label="Cancel" onClick={handleClose} disabled={isLoading} />
          <Button
            data-testid={`confirm-${label}-btn`}
            label={label}
            onClick={handleConfirm}
            variant="contained"
            color={isEnabled ? "error" : "primary"}
            disabled={isLoading}
          />
        </DialogActions>
      </Dialog>
    </Box>
  );
};
